import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Church, Send, CalendarClock, MoreHorizontal, Image as ImageIcon, Film, Music, Smile, Video, Ban, ChevronDown, AtSign, Hash, Camera, Users, Eye } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { FileUpload } from "@/components/FileUpload";
import { StoryCamera } from "@/components/story/StoryCamera";
import { MentionSuggestions } from "@/components/MentionText";
import { PostRichEditor } from "@/components/composer/PostRichEditor";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW } from "@/components/BgTextFrame";
import { Slider } from "@/components/ui/slider";
import { BG_GRADIENTS, BG_IMAGES } from "@/lib/gradients";
import { avatarSrc } from "@/lib/defaults";
import { useBackHandler } from "@/lib/backButton";
import { useBodyScrollLock } from "@/lib/bodyScrollLock";
import { localeOf } from "@/components/story/storyEditorUtils";
import { EmojiTray } from "./ComposerBits";
import { AlbumPreviews, SinglePreview, AudioPreview, ComposerLinkPreview } from "./PostMediaPreviews";
import { PublishAsSheet, PostMoreSheet, PostScheduleSheet, PostCloseSheet, PostMusicSheet, AudienceSheet } from "./PostFsSheets";
import { PostPreviewOverlay } from "./PostPreview";
import { audienceOf } from "@/lib/postAudience";

// Composeur de publication PLEIN ÉCRAN façon Instagram — téléphone (web < 768 px) et app native.
// Toute la logique (brouillon, envoi, album, reel, musique, lien, programmation) reste dans PostComposer :
// ce composant ne fait que l'habillage plein écran et reçoit l'état complet via `c`.

const DARK_VARS = {
  "--yalola-surface": "#121a2b",
  "--yalola-surface-alt": "rgba(255,255,255,0.08)",
  "--yalola-border": "rgba(255,255,255,0.14)",
  "--yalola-text": "#ffffff",
  "--yalola-text-muted": "rgba(255,255,255,0.62)",
  // Bloc « Avant / Après » activé : teinte translucide (le pêche clair du thème rendait le texte blanc illisible).
  "--yalola-primary-soft": "rgba(217,119,87,0.22)",
};
const roundBtn = "h-10 w-10 grid place-items-center rounded-full bg-white/10 text-white backdrop-blur-md active:scale-95 transition-transform";
const chip = "shrink-0 h-9 pl-2.5 pr-3 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 border border-white/15 active:scale-95 transition-transform";
const chipOn = { background: "#fff", color: "#111", borderColor: "#fff" };

export const PostFullscreenComposer = ({ c }) => {
  const { t, user, lang } = useApp();
  const nav = useNavigate();
  const [tool, setTool] = useState(null); // more | schedule | publish_as | close | music | emoji
  const scheduled = c.scheduleOn && !!c.scheduledAt;

  useBodyScrollLock();

  // Le champ texte grandit avec son contenu (hors fond coloré : l'ajustement automatique s'en charge ; champ riche : naturel).
  useEffect(() => {
    const el = c.plainTextareaRef.current;
    if (!el || el.tagName !== "TEXTAREA" || (c.bg && !c.hasMedia)) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [c.content, c.bg, c.hasMedia, c.plainTextareaRef]);

  const closeTool = () => setTool(null);
  const requestClose = () => { if (c.posting) return; if (c.hasDraft) setTool("close"); else c.closeDialog(false); };
  useBackHandler(true, () => { if (tool) closeTool(); else requestClose(); return true; });

  const setSched = (v) => { c.setScheduledAt(v); c.setScheduleOn(!!v); };
  const onPublish = () => { if (c.needsPublishAs && !c.publishAs) { setTool("publish_as"); return; } c.doPublish(); };
  const removeAllMedia = () => { c.attachments.forEach((a) => c.removeAttachment(a.id)); closeTool(); };

  // Destination affichée dans la barre du bas (page d'église, « Publier en tant que », ou mon compte).
  const dest = (() => {
    if (c.churchId) return { church: true, label: c.churchName };
    if (c.needsPublishAs) {
      if (!c.publishAs) return { pick: true, label: t("composer.pick_destination", "Choisir la destination") };
      if (c.publishAs === "me") return { me: true, label: t("composer.me_short", "Moi") };
      return { church: true, label: c.myChurches.find((x) => x.id === c.publishAs)?.name || "" };
    }
    return { me: true, label: user.name };
  })();
  const schedLabel = scheduled ? new Date(c.scheduledAt).toLocaleString(localeOf(lang), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : t("post.schedule", "Programmer la publication");
  const showPalette = !c.hasMedia;
  const bgMode = !!c.bg && !c.hasMedia;

  return (
    <div className="fixed inset-0 z-[80] flex flex-col text-white" style={{ background: "#0b1120" }} data-testid="post-fs-composer" data-tool={tool || ""} data-bg={c.bg || ""}>
      {/* Barre du haut : fermer · titre */}
      <div className="shrink-0 flex items-center justify-between px-3 pb-2" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)" }} data-testid="post-fs-topbar">
        <button type="button" onClick={requestClose} aria-label={t("common.close", "Fermer")} className={roundBtn} data-testid="post-fs-close"><X size={20} /></button>
        <div className="text-sm font-semibold tracking-wide" data-testid="post-fs-title">{t("post.fs_title", "Nouvelle publication")}</div>
        {c.hasDraft
          ? <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "rgba(249,115,22,0.18)", color: "var(--yalola-primary)" }} data-testid="post-fs-draft-chip">{t("composer.draft", "Brouillon")}</span>
          : <span className="w-10" />}
      </div>

      {/* Corps défilant : auteur · texte (ou cadre coloré) · lien · médias */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4" style={DARK_VARS} data-testid="post-fs-body">
        <div className="flex items-center gap-2.5 mb-3">
          <img src={avatarSrc(user)} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" style={{ boxShadow: "0 0 0 2px var(--yalola-primary)" }} />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            {c.churchId && (
              <span className="text-[11px] inline-flex items-center gap-1 truncate" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-church-target">
                <Church size={12} /> {t("composer.publish_to_church", "Publiera sur la page de {name}").replace("{name}", c.churchName)}
              </span>
            )}
          </div>
        </div>

        {bgMode ? (
          <>
            <BgTextFrame ref={c.setBgCanvas} bg={c.bg} className={`grid place-items-center ${BG_TEXT_PAD}`} data-testid="composer-bg-canvas">
              <textarea
                ref={c.bgTextareaRef}
                autoFocus
                placeholder={c.placeholder}
                value={c.content}
                onChange={(e) => c.setContent(e.target.value)}
                className="relative w-full resize-none overflow-hidden border-0 outline-none bg-transparent p-0 text-center font-bold text-white placeholder:text-white/70 min-h-0"
                style={{ ...BG_TEXT_SHADOW, lineHeight: 1.3, outline: "none" }}
                rows={1}
                maxLength={c.bgMax}
                data-testid="composer-textarea"
              />
              <span
                className="absolute bottom-2 right-3 text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md"
                style={{ background: "rgba(0,0,0,0.35)", color: c.content.length >= c.bgMax ? "#fca5a5" : "rgba(255,255,255,0.85)" }}
                aria-live="polite"
                data-testid="composer-bg-counter"
                data-limit={c.content.length >= c.bgMax ? "1" : "0"}
              >
                {c.content.length}/{c.bgMax}
              </span>
            </BgTextFrame>
            <div className="flex items-center gap-3 mt-3" data-testid="composer-text-size">
              <span className="text-xs font-bold text-white h-6 w-6 rounded-md grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)" }} aria-hidden>Aa</span>
              <Slider value={[c.textScale]} min={0.6} max={1.4} step={0.05} onValueChange={([v]) => { c.scaleTouched.current = true; c.setTextScale(v); }} className="flex-1" aria-label={t("composer.text_size", "Taille du texte")} data-testid="composer-text-size-slider" />
              <span className="text-[11px] tabular-nums w-9 text-right" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-text-size-value">{Math.round(c.textScale * 100)}%</span>
            </div>
          </>
        ) : (
          <PostRichEditor
            editorRef={c.plainTextareaRef}
            autoFocus
            placeholder={c.placeholder}
            value={c.content}
            onChange={c.setContent}
            tone="dark"
            className={`w-full text-white leading-relaxed ${c.hasMedia ? "text-base min-h-[3.5rem]" : "text-xl min-h-[6.5rem]"}`}
            toolbarClassName="mb-1.5"
            testId="composer-textarea"
            toolbarTestId="composer-fs-rich-toolbar"
          />
        )}

        {c.linkPreview && !c.hasMedia && <ComposerLinkPreview linkPreview={c.linkPreview} onRemove={c.removeLinkPreview} t={t} />}
        <MentionSuggestions ac={c.mentionAc} className="mt-1" testId="composer-mention-suggestions" t={t} />

        {c.isAlbum
          ? <AlbumPreviews visualAtts={c.visualAtts} reorderVisuals={c.reorderVisuals} removeAttachment={c.removeAttachment} setCaption={c.setCaption} uploadAttachment={c.uploadAttachment} onEditFx={c.editFx} layout={c.layout} setLayout={c.setLayout} canBeforeAfter={c.canBeforeAfter} t={t} />
          : <SinglePreview imageAtts={c.imageAtts} videoAtt={c.videoAtt} effectiveIsReel={c.effectiveIsReel} onVideoMeta={c.onVideoMeta} previewVideoRef={c.previewVideoRef} removeAttachment={c.removeAttachment} uploadAttachment={c.uploadAttachment} onEditFx={c.editFx} t={t} />}
        {c.audioAtt && <AudioPreview audioAtt={c.audioAtt} removeAttachment={c.removeAttachment} uploadAttachment={c.uploadAttachment} t={t} />}

        {c.singleMedia && c.music && (
          <button type="button" onClick={() => setTool("music")} className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white max-w-full" data-testid="post-fs-music-summary">
            <Music size={14} style={{ color: "var(--yalola-primary)" }} /> <span className="truncate">{c.music.title || t("composer.fs_music", "Musique")}</span> <ChevronDown size={13} className="opacity-70" />
          </button>
        )}
      </div>

      {/* Zone basse : fonds · outils · tiroir emoji · barre de publication */}
      <div className="shrink-0 select-none" style={{ ...DARK_VARS, background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(11,17,32,0))" }}>
        {showPalette && (
          c.bgTooLong ? (
            <div className="px-4 pb-2 text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-bg-too-long">
              {t("composer.bg_text_too_long", "Texte trop long pour un fond coloré ({n} caractères max.)").replace("{n}", c.bgMax)}
            </div>
          ) : (
            <div className="px-3 pb-2 flex items-center gap-2 overflow-x-auto hide-scrollbar" data-testid="composer-bg-palette">
              <span className="h-8 w-8 shrink-0 rounded-lg grid place-items-center text-white text-xs font-bold" style={{ background: "linear-gradient(135deg,#7B2FF7,#F107A3,#F5AF19)" }} aria-hidden>Aa</span>
              <button type="button" onClick={() => c.pickBg(null)} aria-label={t("composer.no_bg", "Sans fond")} className="h-8 w-8 shrink-0 rounded-full border-2 grid place-items-center bg-white/10" style={{ borderColor: !c.bg ? "#fff" : "rgba(255,255,255,.35)" }} data-testid="composer-bg-none"><Ban size={14} className="text-white/80" /></button>
              {BG_GRADIENTS.map((g) => (
                <button key={g.key} type="button" onClick={() => c.pickBg(g.key)} aria-label={g.key} className="h-8 w-8 shrink-0 rounded-full border-2 transition-transform active:scale-90" style={{ background: g.css, borderColor: c.bg === g.key ? "#fff" : "rgba(255,255,255,.35)", transform: c.bg === g.key ? "scale(1.15)" : "none" }} data-testid={`composer-bg-${g.key}`} />
              ))}
              {BG_IMAGES.map((b) => (
                <button key={b.key} type="button" onClick={() => c.pickBg(b.key)} aria-label={b.label} className="h-8 w-8 shrink-0 rounded-full border-2 bg-cover bg-center transition-transform active:scale-90" style={{ backgroundImage: `url(${b.url})`, borderColor: c.bg === b.key ? "#fff" : "rgba(255,255,255,.35)", transform: c.bg === b.key ? "scale(1.15)" : "none" }} data-testid={`composer-bg-${b.key.replace("img:", "img-")}`} />
              ))}
            </div>
          )
        )}

        <div className="px-3 pb-2 flex items-center gap-2 overflow-x-auto hide-scrollbar" data-testid="post-fs-chiprow">
          {c.canAddVisual && (
            <button type="button" onClick={() => c.openCamera("photo")} className={chip} data-testid="post-fs-camera-chip"><Camera size={15} className="text-sky-400" /> {t("camera.open", "Caméra")}</button>
          )}
          {c.canAddVisual && (c.native ? (
            <button type="button" onClick={c.addNativeMedia} className={chip} data-testid="composer-modal-media"><ImageIcon size={15} className="text-emerald-400" /> {t("story.tool_gallery", "Galerie")}</button>
          ) : (
            <FileUpload accept={c.mediaAddAccept} multiple onFiles={c.handleFiles} variant="custom" testId="composer-modal-media" className={chip}><ImageIcon size={15} className="text-emerald-400" /> {t("story.tool_gallery", "Galerie")}</FileUpload>
          ))}
          {c.native && c.canAddVideo && (
            <button type="button" onClick={c.addNativeVideo} className={chip} data-testid="composer-modal-native-video-btn"><Film size={15} className="text-indigo-400" /> {t("composer.video", "Vidéo")}</button>
          )}
          {!c.hasVisual && (
            <FileUpload accept="audio/*" onFiles={c.handleFiles} variant="custom" testId="composer-modal-audio" className={chip}><Music size={15} className="text-purple-400" /> {t("composer.fs_audio", "Audio")}</FileUpload>
          )}
          {c.singleMedia && (
            <button type="button" onClick={() => setTool("music")} className={`${chip} relative`} data-testid="post-fs-music-chip">
              <Music size={15} className="text-purple-400" /> {t("composer.fs_music", "Musique")}
              {c.music && <span className="absolute top-1 right-1 h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />}
            </button>
          )}
          <button type="button" onClick={() => setTool(tool === "emoji" ? null : "emoji")} aria-pressed={tool === "emoji"} className={chip} style={tool === "emoji" ? chipOn : undefined} data-testid="composer-modal-emoji-btn"><Smile size={15} className={tool === "emoji" ? "text-amber-600" : "text-amber-400"} /> Emoji</button>
          <button type="button" onClick={() => { closeTool(); c.startMention("@"); }} className={chip} data-testid="composer-modal-mention-btn"><AtSign size={15} className="text-sky-400" /> {t("composer.mention_chip", "Mention")}</button>
          <button type="button" onClick={() => { closeTool(); c.startHashtag(); }} className={chip} data-testid="composer-modal-hashtag-btn"><Hash size={15} className="text-teal-400" /> {t("composer.hashtag_chip", "Hashtag")}</button>
          <button type="button" onClick={() => setTool("schedule")} aria-pressed={scheduled} className={chip} style={scheduled ? chipOn : undefined} data-testid="post-fs-schedule-chip"><CalendarClock size={15} className={scheduled ? "text-orange-600" : "text-orange-400"} /> {scheduled ? schedLabel : t("post.schedule_btn", "Programmer")}</button>
          {c.multiGroupsOn && (
            <button type="button" onClick={() => { closeTool(); c.openGroupsSheet(); }} aria-pressed={c.destCount > 1} className={`${chip} max-w-[60vw]`} style={c.destCount > 1 ? chipOn : undefined} data-testid="post-fs-groups-chip" data-count={c.destCount}>
              <Users size={15} className={c.destCount > 1 ? "text-emerald-700" : "text-emerald-400"} /> <span className="truncate">{c.destCount > 1 ? c.destLabel : t("composer.groups_title", "Où publier ?")}</span>
            </button>
          )}
          {!c.hasMedia && (
            <button type="button" onClick={() => nav("/live?create=1")} className={chip} data-testid="composer-modal-live"><Video size={15} className="text-rose-400" /> {t("composer.fs_live", "Direct")}</button>
          )}
        </div>

        {tool === "emoji" && <EmojiTray onEmoji={c.insertEmoji} onClose={closeTool} t={t} testId="composer-emoji-tray" />}

        <div className="px-3 pt-1 flex items-center gap-2" style={{ paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)" }} data-testid="post-fs-bottombar">
          <button
            type="button"
            onClick={() => { if (c.needsPublishAs) setTool("publish_as"); }}
            disabled={!c.needsPublishAs}
            className={`flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full backdrop-blur-xl border active:scale-95 transition-transform min-w-0 ${dest.church ? "bg-sky-500/20 border-sky-400/40" : "bg-white/15 border-white/20"}`}
            style={dest.pick ? { background: "rgba(249,115,22,0.22)", borderColor: "var(--yalola-primary)" } : undefined}
            data-testid="post-fs-dest-chip"
            data-dest={c.churchId || c.publishAs || (c.needsPublishAs ? "" : "me")}
          >
            {dest.church
              ? <span className="h-8 w-8 rounded-full grid place-items-center bg-sky-500 text-white border-2 border-sky-300 shrink-0"><Church size={16} /></span>
              : dest.pick
                ? <span className="h-8 w-8 rounded-full grid place-items-center text-white shrink-0" style={{ background: "var(--yalola-primary)" }}><ChevronDown size={16} /></span>
                : <img src={avatarSrc(user)} alt="" className="h-8 w-8 rounded-full object-cover border-2 shrink-0" style={{ borderColor: "var(--yalola-primary)" }} />}
            <span className="text-[12px] font-semibold text-white truncate max-w-[120px]">{dest.label}</span>
            {c.needsPublishAs && !dest.pick && <ChevronDown size={14} className="opacity-70 shrink-0" />}
          </button>
          {c.audienceEnabled && (() => {
            const a = audienceOf(c.audience);
            return (
              <button
                type="button"
                onClick={() => setTool("audience")}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-xl border active:scale-95 transition-transform min-w-0 ${c.audience !== "public" ? "bg-emerald-500/20 border-emerald-400/40" : "bg-white/15 border-white/20"}`}
                style={a.accent ? { background: a.accent, borderColor: "transparent" } : undefined}
                data-testid="post-fs-audience-chip"
                data-audience={c.audience}
                aria-label={t("audience.title", "Qui peut voir ta publication ?")}
              >
                <a.icon size={15} className="shrink-0" />
                <span className="text-[12px] font-semibold text-white truncate max-w-[110px]">{t(a.tkey, a.label)}</span>
                <ChevronDown size={13} className="opacity-70 shrink-0" />
              </button>
            );
          })()}
          <div className="flex-1" />
          {c.previewOn && (
          <button type="button" onClick={() => { closeTool(); c.openPreview(); }} disabled={c.posting || (!c.content.trim() && !c.hasMedia)} aria-label={t("post.preview_btn", "Aperçu")} title={t("post.preview_btn", "Aperçu")} className={`${roundBtn} disabled:opacity-40`} data-testid="post-fs-preview-btn">
            <Eye size={20} />
          </button>
          )}
          <button type="button" onClick={() => setTool("more")} aria-label={t("story.more_title", "Options")} className={`${roundBtn} relative`} data-testid="post-fs-more-btn">
            <MoreHorizontal size={20} />
            {scheduled && <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2" style={{ background: "var(--yalola-primary)", borderColor: "#0b1120" }} />}
          </button>
          <button
            type="button"
            onClick={onPublish}
            disabled={c.posting || (!c.content.trim() && !c.hasMedia)}
            className="h-12 pl-4 pr-5 rounded-full text-white text-sm font-bold flex items-center gap-1.5 shadow-lg active:scale-95 transition-transform disabled:opacity-50"
            style={{ background: "var(--yalola-primary)", boxShadow: "0 8px 24px -6px var(--yalola-primary)" }}
            data-testid="composer-publish"
            data-fs="1"
          >
            {c.posting ? t("post.publishing", "Publication...") : scheduled ? <><CalendarClock size={18} /> {t("post.schedule_btn", "Programmer")}</> : <><Send size={18} /> {t("post.publish", "Publier")}</>}
          </button>
        </div>
      </div>

      <PublishAsSheet open={tool === "publish_as"} onClose={closeTool} user={user} churches={c.myChurches} publishAs={c.publishAs} onPick={(v) => { c.setPublishAs(v); closeTool(); }} t={t} />
      <AudienceSheet open={tool === "audience"} onClose={closeTool} value={c.audience} onPick={(v) => { c.setAudience(v); closeTool(); }} t={t} />
      <PostMoreSheet open={tool === "more"} onClose={closeTool} onSchedule={() => setTool("schedule")} scheduledAt={scheduled ? c.scheduledAt : ""} hasMedia={c.hasMedia} onRemoveAllMedia={removeAllMedia} onDiscard={c.discardAndClose} lang={lang} t={t} />
      <PostScheduleSheet open={tool === "schedule"} onClose={closeTool} scheduledAt={c.scheduleOn ? c.scheduledAt : ""} setScheduledAt={setSched} t={t} />
      <PostMusicSheet open={tool === "music"} onClose={closeTool} music={c.music} setMusic={c.setMusic} musicVolume={c.musicVolume} setMusicVolume={c.setMusicVolume} keepVideoSound={c.keepVideoSound} setKeepVideoSound={c.setKeepVideoSound} videoAtt={c.videoAtt} previewVideoRef={c.previewVideoRef} t={t} />
      <PostCloseSheet open={tool === "close"} onClose={closeTool} onKeep={() => c.closeDialog(false)} onDiscard={c.discardAndClose} t={t} />
      <PostPreviewOverlay open={!!c.previewPost && !!c.previewOn} onClose={c.closePreview} post={c.previewPost} onPublish={() => { c.closePreview(); onPublish(); }} publishLabel={c.publishLabel} t={t} />
      <StoryCamera open={c.cameraOpen} key={c.cameraMode} initialMode={c.cameraMode} maxSec={c.cameraMaxSec} onCapture={c.onCameraCapture}
        onFiles={(files) => { c.setCameraOpen(false); c.handleFiles(files); }}
        onText={!c.hasMedia ? () => { c.setCameraOpen(false); if (!c.bg) c.pickBg(BG_GRADIENTS[0].key); } : null}
        onClose={() => c.setCameraOpen(false)} t={t} />
    </div>
  );
};
